import {
  BreakPointCondition,
  Icon,
  Notice,
  Shadow,
  useBreakpointIf,
  HStack,
  VStack,
} from '@genial/components';
import { BoxRadius, Color, Spacing, Typography } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import { useNavigation, useRoute, type RouteProp } from '@react-navigation/native';
import { Box, Button, Text, View } from 'native-base';
import { ScrollView } from 'react-native';
import { useRecoilValue } from 'recoil';

import {
  SignedInNavigationScreenNavigationProp,
  type SignedInRootStackParamList,
} from '../../routes/types';
import { useProductActions } from '../../store/product/actions';
import { sessionState } from '../../store/session/atom';

import { CustomerSection } from './components/CustomerSection';
import { OrderSection } from './components/OrderSection';
import { ProductSection } from './components/ProductSection';
import { ResumeHeader } from './components/ResumeHeader';
import {
  OrderOfferResponseProps,
  OrderResponseProps,
  ProductType,
  orderResumePdfTemplate,
  parsePDFDataExport,
  setupPDF,
  updateInfosToExport,
} from './utils';

export const Resume = () => {
  const { userProfile } = useRecoilValue(sessionState);
  const isManager = !!(userProfile?.isGestor && userProfile?.idTipoParceiro !== 4);

  const isTablet = useBreakpointIf(BreakPointCondition.EqualOrDown, 'lg');
  const { updateProductStatus } = useProductActions();

  const { navigate } = useNavigation<SignedInNavigationScreenNavigationProp>();

  const { params } = useRoute<RouteProp<SignedInRootStackParamList, 'Resume'>>();
  const customerData = { ...params?.customer, ...params?.balance };
  const productData = {
    ...params?.offer,
    ...params?.order,
  } as Partial<OrderOfferResponseProps & OrderResponseProps>;

  const productType = (params?.type || 'FixedIncome') as ProductType;
  const operationType = params?.operationType || 'buy';
  const handleFollowOrder = () => {
    navigate('OrdersHistory');
  };

  const handleExportNewOrder = () => {
    const updatedParams = updateInfosToExport(params);
    const data = parsePDFDataExport(updatedParams);
    setupPDF(orderResumePdfTemplate(data, productType), `ordem-${productData.orderHash}.pdf`);
  };

  const handleCreateNewOrder = () => {
    navigate('NewApplication');
    updateProductStatus({ label: 'Selecione o produto', status: 'opened' });
  };

  const masterSelectionStyle = {
    container: { pb: Spacing['stack.xxs'], justifyContent: 'space-between' },
    selectContainer: { w: 'container' },
  };

  const hasMessageError =
    params?.message &&
    Object.keys(params?.message).length === 3 &&
    Object.values(params?.message).every((value) => typeof value === 'string');

  return (
    <ScrollView>
      <VStack px={{ base: Spacing['inline.xxxs'], sm: Spacing['inline.xxs'], lg: 0 }}>
        <View width="100%" minW={'container'} flex={1}>
          <ResumeHeader
            handleExportNewOrder={handleExportNewOrder}
            isTablet={isTablet}
            selectContainer={masterSelectionStyle.selectContainer}
          />
          <Shadow level={2} testId="qa" borderRadius={BoxRadius.sm}>
            <VStack
              bg={'neutral.white'}
              borderRadius={BoxRadius.sm}
              p={Spacing['inline.xxs']}
              space={Spacing['inline.xxs']}>
              <VStack space={Spacing['inline.nano']}>
                <Box
                  p={Spacing['inline.nano']}
                  borderRadius={BoxRadius.sm}
                  bg={Utils.ColorToken.WithOpacity('neutral.lightest', 0.2)}>
                  <HStack alignItems={'flex-start'} space={Spacing['inline.nano']}>
                    <Icon
                      testID="qa"
                      name="Check"
                      size="md"
                      color={Color['feedback.success.default']}
                    />
                    <VStack space={Spacing['inline.quarck']} flex={1}>
                      <Text
                        fontSize={Typography['fontsize.lg']}
                        variant="semibold"
                        color={Color['neutral.black']}>
                        Ordem {productData?.orderHash || ''} enviada com sucesso.
                      </Text>
                      <Text
                        fontSize={Typography['fontsize.lg']}
                        variant="regular"
                        color={Color['neutral.black']}>
                        Você poderá acompanhar suas ordens clicando no botão{' '}
                        <Text
                          fontSize={Typography['fontsize.lg']}
                          variant="semibold"
                          fontWeight={'semibold'}
                          color={Color['neutral.black']}>
                          acompanhar.
                        </Text>
                      </Text>
                    </VStack>
                  </HStack>
                </Box>

                {!isManager && productType === 'Equity' && (
                  <Notice
                    testId="equity-notice"
                    backgroundColor={Utils.ColorToken.WithOpacity(
                      'feedback.attention.lightest',
                      'opacity.medium'
                    )}
                    type="warning"
                    fontSize={Typography['fontsize.md']}
                    textObjects={[
                      { text: 'Atenção: ', variant: 'bold' },
                      {
                        text: 'Os valores apresentados nesta pré-boleta estão sujeitos a variações de mercado e não contemplam custos operacionais como corretagem, emolumentos ou outros.',
                        variant: 'regular',
                      },
                    ]}
                  />
                )}

                {hasMessageError && (
                  <Notice
                    testId={'without-money-notice'}
                    backgroundColor={Utils.ColorToken.WithOpacity(
                      'feedback.attention.lightest',
                      'opacity.medium'
                    )}
                    type="warning"
                    fontSize={Typography['fontsize.md']}
                    textObjects={[
                      { text: `${params?.message.errorTitle}: `, variant: 'bold' },
                      {
                        text: `${params?.message.errorMessage} ${params?.message.errorSuggest}`,
                        variant: 'regular',
                      },
                    ]}
                  />
                )}
              </VStack>
              <CustomerSection {...customerData} fullCustomerItem={params.fullCustomerItem} />
              <ProductSection
                {...productData}
                productType={productType}
                operationType={operationType}
                fullProductItem={params.fullProductItem}
              />
              <OrderSection {...params?.order} />
            </VStack>
          </Shadow>
          <HStack
            py={Spacing['inline.xxs']}
            space={Spacing['inset.md']}
            justifyContent={{ base: 'space-between', sm: 'flex-end' }}>
            <Button testID="follow-order" variant="secondary" onPress={handleFollowOrder}>
              Acompanhar
            </Button>
            <Button testID="new-order" variant="primary" onPress={handleCreateNewOrder}>
              Nova Boleta
            </Button>
          </HStack>
        </View>
      </VStack>
    </ScrollView>
  );
};
